import type { FactoriesFactoryPullRequest, FactoriesWorkOrder, FactoriesWorkOrderArtifact } from "@/api-client";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";

import { useFactoriesLayout } from "../layout/factoriesLayoutContext";
import { type DraftReadinessTone } from "../lib/draftReadiness";
import { OutputList } from "../pages/work-order-popup-redesign/popupShared";
import { SplitRunReview } from "../pages/work-order-split-run/SplitRunReview";
import type { SplitRunStopChoice } from "../pages/work-order-split-run/splitRunFooter";
import type { SplitRunFixture, SplitRunPhase } from "../pages/work-order-split-run/splitRunMocks";
import { MOBILE_TASK_COPY } from "./mobileCopy";
import { MobileTaskActivity } from "./MobileTaskActivity";

export function PhoneTaskReview({
  model,
  draftStart,
  scope,
  withModelSelect,
  actionsOnly = false,
  startTone,
}: {
  model: {
    fixture: SplitRunFixture;
    pullRequests: FactoriesFactoryPullRequest[];
    canUpdate: boolean;
    lineName?: string;
    onArchive?: () => void | Promise<void>;
    onReject?: () => void | Promise<void>;
    onContinueFixes?: () => void | Promise<void>;
    onStop?: (choice: SplitRunStopChoice) => void | Promise<void>;
    startBusy: boolean;
    actionBusy: boolean;
  };
  draftStart: {
    onStart?: () => void | Promise<void>;
    modelSelect?: ReactNode;
  };
  scope: {
    organizationId?: string;
    factoryId?: string;
    factoryKey?: string;
    orderId: string;
    orderNumber?: string;
  };
  withModelSelect: boolean;
  actionsOnly?: boolean;
  startTone?: DraftReadinessTone;
}) {
  const { fixture, pullRequests, canUpdate, lineName } = model;
  return (
    <SplitRunReview
      footer={fixture.footer}
      organizationId={scope.organizationId}
      factoryId={scope.factoryId}
      factoryKey={scope.factoryKey}
      orderId={scope.orderId}
      orderNumber={scope.orderNumber}
      pullRequests={pullRequests}
      canAct={canUpdate}
      onStart={draftStart.onStart}
      modelSelect={withModelSelect ? draftStart.modelSelect : undefined}
      onArchive={model.onArchive}
      onReject={model.onReject}
      onContinueFixes={model.onContinueFixes}
      onStop={model.onStop}
      startBusy={model.startBusy}
      actionBusy={model.actionBusy}
      startDisabled={!canUpdate || !lineName}
      compact="stacked"
      actionsOnly={actionsOnly}
      startTone={startTone}
    />
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="workspace-section-title">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Activity, pull requests, and files. The phone page shows them under the
 * description. The refine chat does not show them; it follows the desktop
 * popup and shows the agent's live work instead. Activity uses the same
 * console automation cards as the desktop popup, stacked for a narrow
 * screen, so collapsible agent steps match desktop.
 */
export function MobileTaskSections({
  order,
  orderId,
  fixture,
  artifacts,
  pullRequests,
  factoryKey,
  orderNumber,
  canStopRun = false,
  actionBusy = false,
  onStopRun,
  onRerunStep,
}: {
  order: FactoriesWorkOrder;
  orderId: string;
  fixture: SplitRunFixture;
  artifacts: FactoriesWorkOrderArtifact[];
  pullRequests: FactoriesFactoryPullRequest[];
  factoryKey?: string;
  orderNumber?: string;
  canStopRun?: boolean;
  actionBusy?: boolean;
  onStopRun?: (run: { appId: string; runId: string }) => void;
  onRerunStep?: (phase: SplitRunPhase) => void;
}) {
  const { organizationId, factoryId } = useFactoriesLayout();
  return (
    <>
      <Section title={MOBILE_TASK_COPY.activity}>
        <MobileTaskActivity
          organizationId={organizationId}
          factoryId={factoryId}
          orderId={orderId}
          fixture={fixture}
          factoryKey={factoryKey}
          orderNumber={orderNumber ?? (order.number ? String(order.number) : undefined)}
          canStopRun={canStopRun}
          actionBusy={actionBusy}
          onStopRun={onStopRun}
          onRerunStep={onRerunStep}
        />
      </Section>

      {pullRequests.length > 0 ? (
        <Section title={MOBILE_TASK_COPY.pullRequests}>
          <PullRequestList pullRequests={pullRequests} />
        </Section>
      ) : null}

      {artifacts.length > 0 ? (
        <Section title={MOBILE_TASK_COPY.files}>
          <FileList artifacts={artifacts} />
        </Section>
      ) : null}
    </>
  );
}

function PullRequestList({ pullRequests }: { pullRequests: FactoriesFactoryPullRequest[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {pullRequests.map((pullRequest) => (
        <li key={pullRequest.id ?? pullRequest.url}>
          <a
            href={pullRequest.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-[13px] text-foreground"
          >
            <span className="min-w-0 flex-1 break-words">
              <span className="block font-medium">
                {pullRequest.title || `Pull request #${pullRequest.number ?? ""}`}
              </span>
              <span className="block text-[12px] text-muted-foreground">
                {[
                  pullRequest.repository,
                  pullRequest.number ? `#${pullRequest.number}` : null,
                  pullRequestStateLabel(pullRequest),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <ExternalLink className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          </a>
        </li>
      ))}
    </ul>
  );
}

function pullRequestStateLabel(pullRequest: FactoriesFactoryPullRequest): string | null {
  if (pullRequest.state === "STATE_MERGED") return "Merged";
  if (pullRequest.state === "STATE_CLOSED") return "Closed";
  if (pullRequest.state === "STATE_DRAFT") return "Draft";
  if (pullRequest.state === "STATE_OPEN") return "Open";
  return null;
}

function FileList({ artifacts }: { artifacts: FactoriesWorkOrderArtifact[] }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3">
      <OutputList artifacts={artifacts} />
    </div>
  );
}
